// Inline evidence rendering for agent-authored messages. Five
// attachment kinds (screenshot / diff / run_summary / artifact /
// command_result) + the parse + format helpers they share. Extracted
// from ProjectRoomPage 2026-04-24 per Reef audit — a 200-line
// self-contained rendering concern had no business inside the page
// component.
//
// A07 added two interactive kinds: `commit` (when status is
// `awaiting_approval`, renders Approve/Reject buttons that call back
// into the api client) and `needs_human_input` (renders the question
// plus option pills).

import { useEffect, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "../../spacetimedb-bindings";
import {
  ApiError,
  approvePendingCommit,
  attachRepo,
  connectGithubToken,
  generateCommitReceipt,
  getConfigState,
  listGithubRepos,
  startGithubOauth,
  rejectPendingCommit,
  resolveHumanInput,
  setRepoCommitAuthor,
  setWorkspaceAutonomy,
  setWorkspaceDigestHour,
  setWorkspaceEscalationEmail,
  setWorkspaceProviderKey,
  type AutonomyLevel,
  type ConfigStateRepo,
  type GithubRepo,
} from "../api/client";
import type { AttachmentRow } from "./types";
import { openGithubOauthPopup } from "./githubOauthPopup";

export interface ScreenshotPayload {
  url?: string;
  data_uri?: string;
  alt?: string;
}

export interface DiffPayload {
  filename?: string;
  patch?: string;
  added?: number;
  removed?: number;
}

export interface RunSummaryPayload {
  label?: string;
  total?: number;
  pass?: number;
  fail?: number;
  duration_ms?: number;
  note?: string;
  url?: string;
}

export interface CommandResultPayload {
  // Pre-A03 shape (tests + earlier harness):
  cmd?: string;
  exit?: number;
  // A03 shape (working_agent_loop):
  command?: string;
  cwd?: string;
  exit_code?: number;
  error?: string;
  stdout?: string;
  stderr?: string;
  duration_ms?: number;
  timed_out?: boolean;
}

export interface CommitPayload {
  repo?: string;
  branch?: string;
  message?: string;
  files?: string[];
  sha?: string;
  error?: string;
  // "pushed" | "awaiting_approval" | "failed"
  status?: string;
}

export interface ArtifactPayload {
  filename?: string;
  r2_key?: string;
  url?: string;
  size?: number;
  content_type?: string;
}

export interface HumanInputOption {
  id?: string;
  label?: string;
}

export interface HumanInputPayload {
  question?: string;
  options?: HumanInputOption[];
  urgency?: string;
  resolved?: boolean;
}

export interface WatcherNotePayload {
  watcher_id?: number;
  watcher_seat?: string;
  watcher_display_name?: string;
  watcher_role?: string;
  reason?: string;
  note?: string;
}

export interface GateLinkPayload {
  title?: string;
  subtitle?: string;
  summary?: string;
  primary_url?: string;
  primary_label?: string;
  agenda_id?: number;
  step_id?: number;
  gate_id?: number;
  receipt_id?: number;
}

export interface NeedsConfigOption {
  id?: string;
  label?: string;
}

export interface NeedsConfigPayload {
  kind?: string;
  message?: string;
  placeholder?: string;
  options?: NeedsConfigOption[];
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export function parsePayload<T>(payload_json: string): T | null {
  try {
    return JSON.parse(payload_json) as T;
  } catch {
    return null;
  }
}

export interface AttachmentRendererProps {
  attachment: AttachmentRow;
  workspaceId?: number;
  /** A07 — map from `message_id` to the pending_commit row id, so the
   *  commit-attachment renderer can wire Approve/Reject buttons. */
  pendingCommitIdByMessageId?: Map<number, number>;
}

export function AttachmentRenderer({
  attachment,
  workspaceId,
  pendingCommitIdByMessageId,
}: AttachmentRendererProps) {
  if (attachment.kind === "screenshot") {
    const payload = parsePayload<ScreenshotPayload>(attachment.payload_json);
    if (!payload) return null;
    const src = payload.data_uri ?? payload.url;
    if (!src) return null;
    return (
      <a
        href={src}
        target="_blank"
        rel="noreferrer"
        className="block"
        data-testid={`attachment-screenshot-${attachment.attachment_id}`}
      >
        <img
          src={src}
          alt={payload.alt ?? "screenshot"}
          className="max-h-80 object-contain"
        />
      </a>
    );
  }

  if (attachment.kind === "diff") {
    const payload = parsePayload<DiffPayload>(attachment.payload_json);
    if (!payload || !payload.patch) return null;
    return (
      <div
        className="border-l-2 border-edge bg-surface-raised px-3 py-2"
        data-testid={`attachment-diff-${attachment.attachment_id}`}
      >
        {payload.filename && (
          <p className="mb-2 font-code text-[0.625rem] text-faint">
            {payload.filename}
          </p>
        )}
        <pre className="overflow-x-auto font-code text-xs leading-relaxed">
          {payload.patch.split("\n").map((line, idx) => {
            const tone =
              line.startsWith("+") && !line.startsWith("+++")
                ? "bg-success/20 text-primary"
                : line.startsWith("-") && !line.startsWith("---")
                  ? "bg-danger/20 text-primary"
                  : "text-muted";
            return (
              <span key={idx} className={`block ${tone}`}>
                {line || " "}
              </span>
            );
          })}
        </pre>
        {(payload.added !== undefined || payload.removed !== undefined) && (
          <p className="mt-2 text-[0.625rem] text-faint">
            +{payload.added ?? 0} / −{payload.removed ?? 0}
          </p>
        )}
      </div>
    );
  }

  if (attachment.kind === "run_summary") {
    const payload = parsePayload<RunSummaryPayload>(attachment.payload_json);
    if (!payload) return null;
    const fail = payload.fail ?? 0;
    const failed = fail > 0;
    const tone = failed ? "text-danger" : "text-faint";
    const durationLabel =
      payload.duration_ms !== undefined && payload.duration_ms > 0
        ? ` · ${(payload.duration_ms / 1000).toFixed(1)}s`
        : "";
    const noteLabel = payload.note ? ` · ${payload.note}` : "";
    return (
      <p
        className={`font-code text-xs ${tone}`}
        data-testid={`attachment-run-summary-${attachment.attachment_id}`}
      >
        {payload.label ?? "run"}
        {noteLabel}
        {durationLabel}
        {payload.url && (
          <>
            {" · "}
            <a
              href={payload.url}
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              open
            </a>
          </>
        )}
      </p>
    );
  }

  if (attachment.kind === "artifact") {
    const payload = parsePayload<ArtifactPayload>(attachment.payload_json);
    if (!payload || !payload.url) return null;
    const filename = payload.filename ?? "artifact";
    const contentType = payload.content_type ?? "application/octet-stream";
    const isImage = contentType.startsWith("image/");
    const sizeLabel = payload.size ? formatSize(payload.size) : "";
    if (isImage) {
      return (
        <a
          href={payload.url}
          target="_blank"
          rel="noreferrer"
          className="block"
          data-testid={`attachment-artifact-${attachment.attachment_id}`}
        >
          <img
            src={payload.url}
            alt={filename}
            className="max-h-80 rounded-md object-contain"
          />
          <p className="mt-1 font-code text-xs text-faint">
            {filename}
            {sizeLabel && <span> · {sizeLabel}</span>}
          </p>
        </a>
      );
    }
    return (
      <a
        href={payload.url}
        target="_blank"
        rel="noreferrer"
        className="block rounded-md border border-edge bg-surface-raised px-3 py-2 transition-colors hover:border-accent"
        data-testid={`attachment-artifact-${attachment.attachment_id}`}
      >
        <p className="font-code text-xs text-primary">{filename}</p>
        <p className="mt-1 text-xs text-muted">
          {contentType}
          {sizeLabel && <span> · {sizeLabel}</span>}
        </p>
      </a>
    );
  }

  if (attachment.kind === "command_result") {
    const payload = parsePayload<CommandResultPayload>(attachment.payload_json);
    if (!payload) return null;
    const exit = payload.exit_code ?? payload.exit ?? 0;
    const cmd = payload.command ?? payload.cmd ?? "";
    const success = exit === 0 && !payload.timed_out && !payload.error;
    const stdout = payload.stdout ?? "";
    const stderr = payload.stderr ?? payload.error ?? "";
    const hasStderr = stderr.trim().length > 0;
    return (
      <div
        className="rounded-md border-l-2 border-edge bg-surface-raised px-3 py-2"
        data-testid={`attachment-command-result-${attachment.attachment_id}`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span
            className={`text-xs font-medium ${success ? "text-success" : "text-danger"}`}
          >
            {payload.timed_out ? "Timed out" : `exit ${exit}`}
          </span>
          {payload.duration_ms !== undefined && (
            <span className="font-code text-[0.625rem] text-faint">
              {(payload.duration_ms / 1000).toFixed(2)}s
            </span>
          )}
        </div>
        {cmd && (
          <pre className="mt-2 overflow-x-auto font-code text-xs text-primary">
            {payload.cwd ? `${payload.cwd}$ ` : "$ "}
            {cmd}
          </pre>
        )}
        {stdout && (
          <pre
            className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap font-code text-xs text-primary"
            data-testid={`attachment-command-result-${attachment.attachment_id}-stdout`}
          >
            {stdout}
          </pre>
        )}
        {hasStderr && (
          <details className="mt-2">
            <summary className="cursor-pointer text-[0.625rem] text-muted hover:text-primary">
              stderr ({stderr.split("\n").length} line
              {stderr.split("\n").length === 1 ? "" : "s"})
            </summary>
            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap font-code text-xs text-danger">
              {stderr}
            </pre>
          </details>
        )}
      </div>
    );
  }

  if (attachment.kind === "commit") {
    const payload = parsePayload<CommitPayload>(attachment.payload_json);
    if (!payload) return null;
    const status = payload.status ?? "pushed";
    const pendingCommitId =
      status === "awaiting_approval"
        ? pendingCommitIdByMessageId?.get(attachment.message_id)
        : undefined;
    return (
      <CommitAttachment
        attachmentId={attachment.attachment_id}
        messageId={attachment.message_id}
        payload={payload}
        status={status}
        workspaceId={workspaceId}
        pendingCommitId={pendingCommitId}
      />
    );
  }

  if (attachment.kind === "gate_link") {
    const payload = parsePayload<GateLinkPayload>(attachment.payload_json);
    if (!payload) return null;
    const title = payload.title ?? "Gate crossed";
    return (
      <div
        className="rounded-md border-l-2 border-accent bg-surface-raised px-3 py-2"
        data-testid={`attachment-gate-link-${attachment.attachment_id}`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs font-medium text-primary">{title}</span>
          {payload.subtitle && (
            <span className="font-code text-[0.625rem] text-faint">
              {payload.subtitle}
            </span>
          )}
        </div>
        {payload.summary && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-primary">
            {payload.summary}
          </p>
        )}
        {payload.primary_url && (
          <a
            href={payload.primary_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20"
            data-testid={`attachment-gate-link-${attachment.attachment_id}-open`}
          >
            {payload.primary_label ?? "Open"}
            {" →"}
          </a>
        )}
      </div>
    );
  }

  if (attachment.kind === "watcher_note") {
    const payload = parsePayload<WatcherNotePayload>(attachment.payload_json);
    if (!payload || !payload.note) return null;
    return (
      <div
        className="rounded-md border-l-2 border-warning/60 bg-surface-raised px-3 py-2"
        data-testid={`attachment-watcher-note-${attachment.attachment_id}`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs font-medium text-warning">
            {payload.watcher_display_name ?? "Watcher"}
            {payload.watcher_role && (
              <span className="ml-2 text-[0.625rem] text-muted">
                · {payload.watcher_role}
              </span>
            )}
          </span>
          {payload.reason && (
            <span className="font-code text-[0.625rem] text-faint">
              {payload.reason}
            </span>
          )}
        </div>
        <p className="mt-1 whitespace-pre-wrap text-xs text-primary">
          {payload.note}
        </p>
      </div>
    );
  }

  if (attachment.kind === "needs_human_input") {
    const payload = parsePayload<HumanInputPayload>(attachment.payload_json);
    if (!payload) return null;
    return (
      <HumanInputAttachment
        attachmentId={attachment.attachment_id}
        payload={payload}
        workspaceId={workspaceId}
      />
    );
  }

  if (attachment.kind === "needs_config") {
    const payload = parsePayload<NeedsConfigPayload>(attachment.payload_json);
    if (!payload || !payload.kind) return null;
    return (
      <NeedsConfigAttachment
        attachmentId={attachment.attachment_id}
        payload={payload}
        workspaceId={workspaceId}
      />
    );
  }

  return null;
}

function CommitAttachment({
  attachmentId,
  messageId,
  payload,
  status,
  workspaceId,
  pendingCommitId,
}: {
  attachmentId: number;
  messageId: number;
  payload: CommitPayload;
  status: string;
  workspaceId?: number;
  pendingCommitId?: number;
}) {
  const [busy, setBusy] = useState<"approve" | "reject" | "receipt" | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [receiptInfo, setReceiptInfo] = useState<string | null>(null);

  const tone =
    status === "pushed"
      ? "border-accent"
      : status === "awaiting_approval"
        ? "border-warning"
        : status === "rejected"
          ? "border-muted"
          : "border-danger";
  const label =
    status === "pushed"
      ? "Committed"
      : status === "awaiting_approval"
        ? "Awaiting approval"
        : status === "rejected"
          ? "Rejected"
          : "Commit failed";
  const href =
    payload.repo && payload.sha
      ? `https://github.com/TailwindMechanics/${payload.repo}/commit/${payload.sha}`
      : null;

  const handleApprove = async () => {
    if (!workspaceId || !pendingCommitId) return;
    setError(null);
    setBusy("approve");
    try {
      await approvePendingCommit(workspaceId, pendingCommitId);
      void import("../observability/posthog").then((m) =>
        m.capture("commit_approved", {
          workspace_id: workspaceId,
          pending_commit_id: pendingCommitId,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async () => {
    if (!workspaceId || !pendingCommitId) return;
    setError(null);
    setBusy("reject");
    try {
      await rejectPendingCommit(workspaceId, pendingCommitId);
      void import("../observability/posthog").then((m) =>
        m.capture("commit_rejected", {
          workspace_id: workspaceId,
          pending_commit_id: pendingCommitId,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={`rounded-md border-l-2 ${tone} bg-surface-raised px-3 py-2`}
      data-testid={`attachment-commit-${attachmentId}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium text-primary">{label}</span>
        {payload.sha && (
          <span className="font-code text-[0.625rem] text-faint">
            {payload.sha.slice(0, 7)}
          </span>
        )}
      </div>
      {payload.message && (
        <p className="mt-1 text-xs text-primary">{payload.message}</p>
      )}
      {(payload.repo || payload.branch) && (
        <p className="mt-1 font-code text-[0.625rem] text-muted">
          {payload.repo}
          {payload.branch && <span> · {payload.branch}</span>}
        </p>
      )}
      {payload.files && payload.files.length > 0 && (
        <ul className="mt-2 font-code text-[0.625rem] text-muted">
          {payload.files.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
      {payload.error && (
        <p className="mt-2 text-xs text-danger">{payload.error}</p>
      )}
      {href && status === "pushed" && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block text-[0.625rem] text-accent hover:underline"
        >
          view on GitHub →
        </a>
      )}
      {status === "awaiting_approval" && workspaceId && pendingCommitId && (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleApprove}
            disabled={busy !== null}
            className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
            data-testid={`attachment-commit-${attachmentId}-approve`}
          >
            {busy === "approve" ? "Approving…" : "Approve + push"}
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={busy !== null}
            className="rounded-md border border-muted px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-danger hover:text-danger disabled:opacity-50"
            data-testid={`attachment-commit-${attachmentId}-reject`}
          >
            {busy === "reject" ? "Rejecting…" : "Reject"}
          </button>
        </div>
      )}
      {payload.sha && workspaceId && (
        <CicdCheckStrip workspaceId={workspaceId} sha={payload.sha} />
      )}
      {workspaceId && status === "pushed" && (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              setBusy("receipt");
              setError(null);
              setReceiptInfo(null);
              try {
                await generateCommitReceipt(workspaceId, messageId);
                void import("../observability/posthog").then((m) =>
                  m.capture("commit_receipt_generated", {
                    workspace_id: workspaceId,
                    message_id: messageId,
                  }),
                );
                setReceiptInfo("Commit receipt generated.");
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
            disabled={busy !== null}
            className="rounded-md border border-edge px-2 py-0.5 text-[0.625rem] text-muted hover:border-accent hover:text-accent disabled:opacity-50"
            data-testid={`attachment-commit-${attachmentId}-receipt`}
          >
            {busy === "receipt" ? "Generating…" : "Generate receipt"}
          </button>
          {receiptInfo && (
            <span className="text-[0.625rem] text-success">{receiptInfo}</span>
          )}
        </div>
      )}
      {error && (
        <p className="mt-2 text-[0.625rem] text-danger">{error}</p>
      )}
    </div>
  );
}

function HumanInputAttachment({
  attachmentId,
  payload,
  workspaceId,
}: {
  attachmentId: number;
  payload: HumanInputPayload;
  workspaceId?: number;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [customDraft, setCustomDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<boolean>(payload.resolved === true);

  const urgency = payload.urgency ?? "preference";
  const urgencyTone =
    urgency === "blocker"
      ? "border-danger text-danger"
      : urgency === "decision"
        ? "border-warning text-warning"
        : "border-edge text-muted";

  const submit = async (value: string, key: string) => {
    if (!workspaceId) return;
    if (!value.trim()) return;
    setError(null);
    setBusy(key);
    try {
      await resolveHumanInput(workspaceId, attachmentId, value.trim());
      setResolved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="rounded-md border-l-2 border-accent bg-surface-raised px-3 py-2"
      data-testid={`attachment-human-input-${attachmentId}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium text-primary">
          Captain input requested
        </span>
        <span
          className={`rounded-sm border px-1.5 py-0.5 text-[0.5rem] uppercase tracking-wide ${urgencyTone}`}
        >
          {urgency}
        </span>
      </div>
      {payload.question && (
        <p className="mt-1 text-xs text-primary">{payload.question}</p>
      )}
      {resolved ? (
        <p className="mt-2 text-[0.625rem] text-muted">Answered.</p>
      ) : (
        <>
          {payload.options && payload.options.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {payload.options.map((opt, idx) => {
                const label = opt.label ?? opt.id ?? `option ${idx + 1}`;
                const key = `opt-${opt.id ?? idx}`;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void submit(label, key)}
                    disabled={busy !== null || !workspaceId}
                    className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
                    data-testid={`attachment-human-input-${attachmentId}-${key}`}
                  >
                    {busy === key ? "Sending…" : label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              placeholder="Or type a reply…"
              className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
              data-testid={`attachment-human-input-${attachmentId}-custom`}
            />
            <button
              type="button"
              onClick={() => void submit(customDraft, "custom")}
              disabled={busy !== null || !customDraft.trim() || !workspaceId}
              className="rounded-md border border-muted px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
            >
              {busy === "custom" ? "Sending…" : "Send"}
            </button>
          </div>
        </>
      )}
      {error && <p className="mt-2 text-[0.625rem] text-danger">{error}</p>}
    </div>
  );
}

function GithubConnectAttachment({
  workspaceId,
  message,
}: {
  workspaceId?: number;
  message?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectedLogin, setConnectedLogin] = useState<string | null>(null);
  const [attachedRepoCount, setAttachedRepoCount] = useState<number | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const refreshConnectionState = async () => {
    if (!workspaceId) return;
    try {
      const state = await getConfigState(workspaceId);
      const hasGithub = state.provider_keys.some(
        (k) => k.provider === "github",
      );
      setConnected(hasGithub);
      setAttachedRepoCount(state.repo_connections.length);
    } catch {
      /* keep the last-known affordance visible */
    }
  };

  // On mount, check whether GitHub is ALREADY connected for this
  // workspace's org. If yes, the attachment auto-morphs to a
  // "connected — pick a repo" affordance instead of demanding auth
  // a second time. This is what fixes the "I came back from OAuth
  // but Vela still wants me to authorise" UX bug — the chat
  // attachment is stale by design (immutable history), so the
  // attachment itself has to reflect the new state.
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const state = await getConfigState(workspaceId);
        const hasGithub = state.provider_keys.some(
          (k) => k.provider === "github",
        );
        if (!cancelled) {
          setConnected(hasGithub);
          setAttachedRepoCount(state.repo_connections.length);
        }
      } catch {
        /* silent — placeholder UX is the fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const handleClick = async () => {
    if (!workspaceId) return;
    setError(null);
    setBusy(true);
    try {
      const { url } = await startGithubOauth(workspaceId);
      // Pop-out OAuth in a new tab. The OAuth callback redirects
      // the popup to /workspaces/:id?github=connected; the page's
      // mount effect detects window.opener + the query, posts
      // wt-gh-connected back to us, and closes itself. We morph
      // to the connected state without a full-page reload.
      const result = await openGithubOauthPopup(url, workspaceId);
      if (result.ok) {
        setConnected(true);
        void refreshConnectionState();
      } else if (result.reason === "closed") {
        setError("OAuth popup was closed before authorization completed.");
      }
    } catch (err) {
      const msg = (() => {
        if (err instanceof ApiError) {
          // Friendlier copy for the most common deploy-config error.
          if (err.status === 503 && /OAUTH/.test(err.message)) {
            setTokenOpen(true);
            return "GitHub OAuth is not configured here yet. Paste a GitHub token below to keep moving.";
          }
          return `HTTP ${err.status}: ${err.message}`;
        }
        return err instanceof Error ? err.message : "Could not start GitHub OAuth";
      })();
      setError(msg);
      // Also log so DevTools network + console correlate.
      console.error("[github-oauth] start failed", err);
    } finally {
      setBusy(false);
    }
  };
  const handleTokenSave = async () => {
    if (!workspaceId || !tokenValue.trim()) return;
    setError(null);
    setTokenBusy(true);
    try {
      const saved = await connectGithubToken(workspaceId, tokenValue.trim());
      setConnectedLogin(saved.login);
      setConnected(true);
      void refreshConnectionState();
      setTokenValue("");
      setTokenOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `HTTP ${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : "Could not save GitHub token",
      );
    } finally {
      setTokenBusy(false);
    }
  };
  // Already-connected morph — replaces the auth CTA with a clear
  // "✓ Connected" pill so a returning Captain isn't asked to
  // authorise twice.
  if (connected) {
    return (
      <div
        className="rounded-md border-l-2 border-success bg-surface-raised px-3 py-2"
        data-testid="attachment-github-connect"
      >
        <p className="text-xs font-medium text-primary">
          GitHub connected
          {connectedLogin ? ` as ${connectedLogin}` : ""}
        </p>
        <p className="mt-1 text-[0.625rem] text-muted">
          {attachedRepoCount && attachedRepoCount > 0
            ? `${attachedRepoCount} repo${attachedRepoCount === 1 ? "" : "s"} attached. I am cloning them into the sandbox under /workspace/<repo> so the team can start work.`
            : "Choose the repo or repos this Workspace should work on. I will attach them, clone them into the sandbox under /workspace/<repo>, and surface the tree in the left rail."}
        </p>
        {(attachedRepoCount === null || attachedRepoCount === 0) && (
          <div className="mt-2">
            <RepoPickAttachment
              attachmentId={0}
              message="Pick one or more repos for this Workspace."
              workspaceId={workspaceId}
              onAttached={() => void refreshConnectionState()}
            />
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      className="rounded-md border-l-2 border-accent bg-surface-raised px-3 py-2"
      data-testid="attachment-github-connect"
    >
      <p className="text-xs font-medium text-primary">Connect GitHub</p>
      {message && <p className="mt-1 text-[0.625rem] text-muted">{message}</p>}
      <button
        type="button"
        disabled={busy || !workspaceId}
        onClick={() => void handleClick()}
        className="mt-2 inline-block cursor-pointer rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-40"
        data-testid="github-oauth-start"
      >
        {busy ? "Authorising in popup…" : "Authorize GitHub →"}
      </button>
      <button
        type="button"
        disabled={!workspaceId}
        onClick={() => setTokenOpen((v) => !v)}
        className="ml-2 mt-2 inline-block cursor-pointer rounded-md border border-edge px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        Use token instead
      </button>
      {tokenOpen && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="password"
            value={tokenValue}
            onChange={(e) => setTokenValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleTokenSave();
              }
            }}
            placeholder="github_pat_..."
            className="min-w-0 flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
            autoComplete="off"
            data-testid="github-token-input"
          />
          <button
            type="button"
            onClick={() => void handleTokenSave()}
            disabled={tokenBusy || !workspaceId || !tokenValue.trim()}
            className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
            data-testid="github-token-save"
          >
            {tokenBusy ? "Saving..." : "Save"}
          </button>
        </div>
      )}
      {connectedLogin && (
        <p className="mt-2 text-[0.625rem] text-success">
          GitHub connected as {connectedLogin}.
        </p>
      )}
      {error && (
        <p className="mt-2 text-[0.625rem] text-danger" data-testid="github-oauth-error">
          {error}
        </p>
      )}
    </div>
  );
}

function NeedsConfigAttachment({
  attachmentId,
  payload,
  workspaceId,
}: {
  attachmentId: number;
  payload: NeedsConfigPayload;
  workspaceId?: number;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [value, setValue] = useState("");

  const kind = payload.kind ?? "";
  const isProviderKey = kind.endsWith("_key");

  const submit = async (overrideValue?: string) => {
    if (!workspaceId) return;
    const v = (overrideValue ?? value).trim();
    if (!v && !overrideValue) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      if (isProviderKey) {
        const provider = kind.replace(/_key$/, "");
        await setWorkspaceProviderKey(workspaceId, provider, v);
        setDone(`${provider} key saved`);
      } else if (kind === "escalation_email") {
        await setWorkspaceEscalationEmail(workspaceId, v);
        setDone("escalation email saved");
      } else if (kind === "digest_hour") {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0 || (n > 23 && n !== 255)) {
          throw new Error("hour must be 0-23, or 255 for off");
        }
        await setWorkspaceDigestHour(workspaceId, n);
        setDone("digest hour saved");
      } else if (kind === "autonomy") {
        await setWorkspaceAutonomy(workspaceId, v as AutonomyLevel);
        setDone(`autonomy: ${v}`);
      } else {
        throw new Error(`unsupported config kind: ${kind}`);
      }
      setValue("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (kind === "github_connect") {
    return <GithubConnectAttachment workspaceId={workspaceId} message={payload.message} />;
  }

  if (kind === "repo_pick") {
    return (
      <RepoPickAttachment
        attachmentId={attachmentId}
        message={payload.message}
        workspaceId={workspaceId}
      />
    );
  }

  if (kind === "commit_author") {
    return (
      <CommitAuthorAttachment
        attachmentId={attachmentId}
        message={payload.message}
        workspaceId={workspaceId}
      />
    );
  }

  const tone = done ? "border-success" : "border-accent";

  return (
    <div
      className={`rounded-md border-l-2 ${tone} bg-surface-raised px-3 py-2`}
      data-testid={`attachment-needs-config-${attachmentId}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium text-primary">
          Vela needs: {kind.replace(/_/g, " ")}
        </span>
        <span className="font-code text-[0.625rem] text-faint">{kind}</span>
      </div>
      {payload.message && (
        <p className="mt-1 text-xs text-primary">{payload.message}</p>
      )}
      {done ? (
        <p className="mt-2 text-[0.625rem] text-success">{done}</p>
      ) : kind === "autonomy" ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {(["paused_on_commit", "paused_on_destructive", "freeform"] as const).map(
            (level) => (
              <button
                key={level}
                type="button"
                onClick={() => void submit(level)}
                disabled={busy || !workspaceId}
                className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
              >
                {busy ? "…" : level}
              </button>
            ),
          )}
        </div>
      ) : payload.options && payload.options.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {payload.options.map((opt, idx) => {
            const label = opt.label ?? opt.id ?? `option ${idx + 1}`;
            const id = opt.id ?? label;
            return (
              <button
                key={id}
                type="button"
                onClick={() => void submit(id)}
                disabled={busy || !workspaceId}
                className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
              >
                {busy ? "…" : label}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <input
            type={isProviderKey ? "password" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder={
              payload.placeholder ?? (isProviderKey ? "paste key…" : "value")
            }
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
            data-testid={`attachment-needs-config-${attachmentId}-input`}
            autoComplete={isProviderKey ? "off" : undefined}
          />
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !value.trim() || !workspaceId}
            className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
      {error && <p className="mt-2 text-[0.625rem] text-danger">{error}</p>}
    </div>
  );
}

function CommitAuthorAttachment({
  attachmentId,
  message,
  workspaceId,
}: {
  attachmentId: number;
  message: string | undefined;
  workspaceId: number | undefined;
}) {
  const [repos, setRepos] = useState<ConfigStateRepo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<
    Record<number, { name: string; email: string }>
  >({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [done, setDone] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    getConfigState(workspaceId)
      .then((s) => {
        if (cancelled) return;
        setRepos(s.repo_connections);
        const seed: Record<number, { name: string; email: string }> = {};
        for (const r of s.repo_connections) {
          seed[r.repo_connection_id] = {
            name: r.commit_author_name ?? "",
            email: r.commit_author_email ?? "",
          };
        }
        setDrafts(seed);
      })
      .catch((e) => {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const onSave = async (repoConnectionId: number) => {
    const draft = drafts[repoConnectionId];
    if (!draft) return;
    if (!draft.name.trim() || !draft.email.includes("@")) {
      setActionError("name + valid email required");
      return;
    }
    setBusyId(repoConnectionId);
    setActionError(null);
    try {
      await setRepoCommitAuthor(
        repoConnectionId,
        draft.name.trim(),
        draft.email.trim(),
      );
      setDone((prev) => {
        const next = new Set(prev);
        next.add(repoConnectionId);
        return next;
      });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="rounded-md border-l-2 border-accent bg-surface-raised px-3 py-2"
      data-testid={`attachment-needs-config-${attachmentId}`}
    >
      <p className="text-xs font-medium text-primary">
        Set commit author per repo
      </p>
      {message && (
        <p className="mt-1 text-[0.625rem] text-muted">{message}</p>
      )}
      {loadError && (
        <p className="mt-2 text-[0.625rem] text-danger">{loadError}</p>
      )}
      {!repos && !loadError && (
        <p className="mt-2 text-[0.625rem] text-muted">Loading repos…</p>
      )}
      {repos && repos.length === 0 && (
        <p className="mt-2 text-[0.625rem] text-muted">
          No repos connected yet. Pick repos first via the previous step.
        </p>
      )}
      {repos && repos.length > 0 && (
        <ul className="mt-2 flex flex-col gap-2">
          {repos.map((repo) => {
            const draft = drafts[repo.repo_connection_id] ?? {
              name: "",
              email: "",
            };
            const isBusy = busyId === repo.repo_connection_id;
            const isDone = done.has(repo.repo_connection_id);
            return (
              <li
                key={repo.repo_connection_id}
                className="rounded-md border border-edge bg-surface px-2 py-1"
              >
                <p className="font-code text-[0.625rem] text-primary">
                  {repo.github_owner}/{repo.github_repo}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [repo.repo_connection_id]: {
                          ...draft,
                          name: e.target.value,
                        },
                      }))
                    }
                    placeholder="name"
                    className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-[0.625rem] text-primary placeholder:text-faint"
                  />
                  <input
                    type="email"
                    value={draft.email}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [repo.repo_connection_id]: {
                          ...draft,
                          email: e.target.value,
                        },
                      }))
                    }
                    placeholder="email"
                    className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-[0.625rem] text-primary placeholder:text-faint"
                  />
                  <button
                    type="button"
                    onClick={() => void onSave(repo.repo_connection_id)}
                    disabled={
                      isBusy ||
                      isDone ||
                      !draft.name.trim() ||
                      !draft.email.includes("@")
                    }
                    className="rounded-md border border-accent px-2 py-0.5 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
                  >
                    {isDone ? "Saved" : isBusy ? "…" : "Save"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {actionError && (
        <p className="mt-2 text-[0.625rem] text-danger">{actionError}</p>
      )}
    </div>
  );
}

function RepoPickAttachment({
  attachmentId,
  message,
  workspaceId,
  onAttached,
}: {
  attachmentId: number;
  message: string | undefined;
  workspaceId: number | undefined;
  onAttached?: () => void;
}) {
  const [repos, setRepos] = useState<GithubRepo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [attached, setAttached] = useState<Set<number>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    listGithubRepos(workspaceId)
      .then((r) => {
        if (!cancelled) setRepos(r);
      })
      .catch((e) => {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : String(e);
          setLoadError(message);
          if (
            e instanceof ApiError &&
            (e.status === 400 || e.status === 503)
          ) {
            setTokenOpen(true);
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const onPick = async (repo: GithubRepo) => {
    if (!workspaceId) return;
    const [owner, name] = repo.full_name.split("/");
    if (!owner || !name) return;
    setBusyId(repo.id);
    setActionError(null);
    try {
      await attachRepo(workspaceId, owner, name, repo.default_branch);
      setAttached((prev) => {
        const next = new Set(prev);
        next.add(repo.id);
        return next;
      });
      onAttached?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const saveToken = async () => {
    if (!workspaceId || !tokenValue.trim()) return;
    setTokenBusy(true);
    setLoadError(null);
    setActionError(null);
    try {
      await connectGithubToken(workspaceId, tokenValue.trim());
      const next = await listGithubRepos(workspaceId);
      setRepos(next);
      setTokenValue("");
      setTokenOpen(false);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setTokenBusy(false);
    }
  };

  return (
    <div
      className="rounded-md border-l-2 border-accent bg-surface-raised px-3 py-2"
      data-testid={`attachment-needs-config-${attachmentId}`}
    >
      <p className="text-xs font-medium text-primary">Pick a repo to connect</p>
      {message && (
        <p className="mt-1 text-[0.625rem] text-muted">{message}</p>
      )}
      {loadError && (
        <p className="mt-2 text-[0.625rem] text-danger">
          Could not load your GitHub repos: {loadError}.
        </p>
      )}
      {tokenOpen && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-edge bg-surface p-2">
          <input
            type="password"
            value={tokenValue}
            onChange={(e) => setTokenValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void saveToken();
              }
            }}
            placeholder="github_pat_..."
            autoComplete="off"
            className="min-w-0 flex-1 rounded-md border border-edge bg-surface-raised px-2 py-1 text-xs text-primary placeholder:text-faint"
            data-testid={`attachment-needs-config-${attachmentId}-github-token`}
          />
          <button
            type="button"
            onClick={() => void saveToken()}
            disabled={tokenBusy || !workspaceId || !tokenValue.trim()}
            className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            {tokenBusy ? "Saving..." : "Connect"}
          </button>
        </div>
      )}
      {!repos && !loadError && !tokenOpen && (
        <p className="mt-2 text-[0.625rem] text-muted">Loading your repos…</p>
      )}
      {repos && repos.length === 0 && (
        <p className="mt-2 text-[0.625rem] text-muted">
          GitHub returned no repos. Make sure you authorized the Waytide GitHub
          app for the right account / org.
        </p>
      )}
      {repos && repos.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {repos.map((repo) => {
            const isAttached = attached.has(repo.id);
            const isBusy = busyId === repo.id;
            return (
              <li
                key={repo.id}
                className="flex items-center justify-between gap-2 rounded-md border border-edge bg-surface px-2 py-1"
              >
                <div className="flex flex-col">
                  <span className="font-code text-[0.625rem] text-primary">
                    {repo.full_name}
                  </span>
                  <span className="font-code text-[0.625rem] text-faint">
                    {repo.default_branch}
                    {repo.private && <span className="ml-1">· private</span>}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => void onPick(repo)}
                  disabled={isBusy || isAttached || !workspaceId}
                  className="rounded-md border border-accent px-2 py-0.5 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
                  data-testid={`attachment-needs-config-${attachmentId}-pick-${repo.id}`}
                >
                  {isAttached
                    ? "Cloning"
                    : isBusy
                      ? "..."
                      : "Attach + clone"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {actionError && (
        <p className="mt-2 text-[0.625rem] text-danger">{actionError}</p>
      )}
    </div>
  );
}

function CicdCheckStrip({
  workspaceId,
  sha,
}: {
  workspaceId: number;
  sha: string;
}) {
  const [rows] = useTable(tables.cicd_result);
  const matched = useMemo(() => {
    return rows
      .filter((r) => {
        const ws =
          typeof r.workspaceId === "bigint"
            ? Number(r.workspaceId)
            : Number(r.workspaceId);
        return ws === workspaceId && r.commitSha === sha;
      })
      .map((r) => ({
        result_id:
          typeof r.resultId === "bigint" ? Number(r.resultId) : Number(r.resultId),
        source: r.source,
        context: r.context,
        status: r.status,
        url: r.url,
        summary: r.summary,
      }))
      .sort((a, b) => a.context.localeCompare(b.context));
  }, [rows, workspaceId, sha]);

  if (matched.length === 0) return null;

  return (
    <div
      className="mt-2 flex flex-wrap gap-1"
      data-testid="cicd-check-strip"
    >
      {matched.map((r) => {
        const tone =
          r.status === "success"
            ? "border-success text-success"
            : r.status === "failed"
              ? "border-danger text-danger"
              : r.status === "cancelled"
                ? "border-edge text-muted"
                : "border-warning text-warning";
        const inner = (
          <span
            className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-code text-[0.625rem] ${tone}`}
            title={r.summary || `${r.source} · ${r.context}`}
          >
            {r.context}
            <span className="text-faint">· {r.status}</span>
          </span>
        );
        return r.url ? (
          <a key={r.result_id} href={r.url} target="_blank" rel="noreferrer">
            {inner}
          </a>
        ) : (
          <span key={r.result_id}>{inner}</span>
        );
      })}
    </div>
  );
}
